import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Pagination,
} from "@mui/material";
import { TopEntityStat, OrderSearchParams } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";

interface PaginatedStatsListProps {
    title: string;
    icon: React.ReactNode;
    fetchData: (
        params: OrderSearchParams,
        skip: number,
        limit: number
    ) => Promise<{ items: TopEntityStat[]; total: number }>;
    searchParams: OrderSearchParams;
    initialData?: TopEntityStat[];
    unitLabel?: string;
}

export const PaginatedStatsList: React.FC<PaginatedStatsListProps> = ({
    title,
    icon,
    fetchData,
    searchParams,
    initialData = [],
    unitLabel = "units/orders",
}) => {
    const [items, setItems] = useState<TopEntityStat[]>(initialData);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const limit = 10; // Items per page

    // Initial load
    useEffect(() => {
        // If we have initial data (from the main stats call), use it for the first page
        if (initialData.length > 0 && page === 1 && !loading) {
            setItems(initialData);
            // We might not know total yet, but that's okay, next page load will fix it
            // Or we assume fetching page 1 will update total if we force fetch
        }
        loadPage(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchParams]);

    const loadPage = async (pageNum: number) => {
        setLoading(true);
        try {
            const skip = (pageNum - 1) * limit;
            const data = await fetchData(searchParams, skip, limit);
            setItems(data.items);
            setTotal(data.total);
        } catch (error) {
            console.error(`Failed to load ${title}:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        value: number
    ) => {
        setPage(value);
    };

    return (
        <Paper
            elevation={0}
            variant="outlined"
            sx={{
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                {icon}
                <Typography variant="h6">{title}</Typography>
                {loading && <CircularProgress size={16} sx={{ ml: "auto" }} />}
            </Box>

            <List
                dense
                sx={{
                    flexGrow: 1,
                    overflow: "auto",
                    minHeight: 250,
                    maxHeight: 350,
                }}
            >
                {items.length > 0
                    ? items.map((item, index) => {
                          const serial = (page - 1) * limit + index + 1;
                          return (
                              <React.Fragment key={item.id}>
                                  <ListItem>
                                      <ListItemText
                                          primary={
                                              <Box
                                                  component="span"
                                                  sx={{
                                                      display: "flex",
                                                      gap: 1,
                                                  }}
                                              >
                                                  <Typography
                                                      component="span"
                                                      color="text.secondary"
                                                      fontWeight="bold"
                                                      sx={{ minWidth: "24px" }}
                                                  >
                                                      {serial}.
                                                  </Typography>
                                                  {item.name}
                                              </Box>
                                          }
                                          secondary={`${item.count} ${unitLabel}`}
                                      />
                                      <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                      >
                                          {formatCurrencyBDT(item.amount)}
                                      </Typography>
                                  </ListItem>
                                  <Divider component="li" />
                              </React.Fragment>
                          );
                      })
                    : !loading && (
                          <Typography
                              color="text.secondary"
                              align="center"
                              sx={{ py: 4 }}
                          >
                              No data available
                          </Typography>
                      )}
            </List>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Pagination
                    count={Math.ceil(total / limit)}
                    page={page}
                    onChange={handlePageChange}
                    size="small"
                    color="primary"
                    disabled={loading}
                />
            </Box>
        </Paper>
    );
};
